/**
 * @file Database-driven targeting utilities
 * @module targeting/targetingArrays
 */

/**
 * Match targeting rule rows when DB returns string ids and callers pass numbers.
 */
function targetingRuleMatchesId(rule, id) {
  if (!rule || id == null) return false;
  return Number(rule.id) === Number(id);
}

/**
 * Check if a drone needs targeting based on database rules
 * @param {number} droneId - The drone ID to check
 * @param {Array} targetingData - Database targeting data
 * @returns {boolean} True if the drone needs targeting
 */
export const droneNeedsTargeting = (droneId, targetingData) => {
  if (!targetingData?.drones) return false;
  const droneRule = targetingData.drones.find((rule) => targetingRuleMatchesId(rule, droneId));
  return droneRule && droneRule.targeting !== 'none';
};

/**
 * Check if a drone can target opponent units based on database rules
 * @param {number} droneId - The drone ID to check
 * @param {Array} targetingData - Database targeting data
 * @returns {boolean} True if the drone can target opponents
 */
export const droneCanTargetOpponents = (droneId, targetingData) => {
  if (!targetingData?.drones) return false;
  const droneRule = targetingData.drones.find((rule) => targetingRuleMatchesId(rule, droneId));
  return droneRule && (droneRule.player === 'opponent' || droneRule.player === 'both');
};

/**
 * Check if a drone doesn't need targeting based on database rules
 * @param {number} droneId - The drone ID to check
 * @param {Array} targetingData - Database targeting data
 * @returns {boolean} True if the drone doesn't need targeting
 */
export const droneNoTargeting = (droneId, targetingData) => {
  if (!targetingData?.drones) return true;
  const droneRule = targetingData.drones.find((rule) => targetingRuleMatchesId(rule, droneId));
  return !droneRule || droneRule.targeting === 'none';
};

/**
 * Get targeting rules for a specific drone
 * @param {number} droneId - The drone ID
 * @param {Array} targetingData - Database targeting data
 * @returns {Object|null} The targeting rules or null if not found
 */
export const getDroneTargetingRules = (droneId, targetingData) => {
  if (!targetingData?.drones) return null;
  return targetingData.drones.find((rule) => targetingRuleMatchesId(rule, droneId)) || null;
};

/** Drones that pick a unit card from hand (e.g. Valor Vault 410). */
export const droneTargetsHandUnits = (droneId, targetingData) => {
  const rule = getDroneTargetingRules(droneId, targetingData);
  return rule?.targeting === 'hand unit';
};

/**
 * Check if a unit needs targeting on deploy based on database rules
 * @param {number} unitId - The unit ID to check
 * @param {Array} targetingData - Database targeting data
 * @returns {boolean} True if the unit needs targeting on deploy
 */
export const unitNeedsTargetingOnDeploy = (unitId, targetingData) => {
  if (!targetingData?.deployUnits) return false;
  const deployRule = targetingData.deployUnits.find((rule) => targetingRuleMatchesId(rule, unitId));
  return deployRule && deployRule.targeting !== 'none' && deployRule.targeting !== 'random';
};

/**
 * Get targeting rules for a specific unit on deploy
 * @param {number} unitId - The unit ID
 * @param {Array} targetingData - Database targeting data
 * @returns {Object|null} The targeting rules or null if not found
 */
export const getDeployTargetingRules = (unitId, targetingData) => {
  if (!targetingData?.deployUnits) return null;
  return targetingData.deployUnits.find((rule) => targetingRuleMatchesId(rule, unitId)) || null;
};

/**
 * Check if a strategem needs targeting based on database rules
 * @param {number} strategemId - The strategem ID to check
 * @param {Array} targetingData - Database targeting data
 * @returns {boolean} True if the strategem needs targeting
 */
export const strategemNeedsTargeting = (strategemId, targetingData) => {
  if (!targetingData?.strategems) return false;
  const strategemRule = targetingData.strategems.find((rule) => targetingRuleMatchesId(rule, strategemId));
  return strategemRule && strategemRule.targeting !== 'none';
};

/**
 * Get targeting rules for a specific strategem
 * @param {number} strategemId - The strategem ID
 * @param {Array} targetingData - Database targeting data
 * @returns {Object|null} The targeting rules or null if not found
 */
export const getStrategemTargetingRules = (strategemId, targetingData) => {
  if (!targetingData?.strategems) return null;
  return targetingData.strategems.find((rule) => targetingRuleMatchesId(rule, strategemId)) || null;
};

/**
 * Check if a weapon needs targeting based on database rules
 * @param {number} weaponId - The weapon ID to check
 * @param {Array} targetingData - Database targeting data
 * @returns {boolean} True if the weapon needs targeting
 */
export const weaponNeedsTargeting = (weaponId, targetingData) => {
  if (!targetingData?.weapons) return false;
  const weaponRule = targetingData.weapons.find((rule) => targetingRuleMatchesId(rule, weaponId));
  return weaponRule && weaponRule.targeting !== 'none';
};

/**
 * Get targeting rules for a specific weapon
 * @param {number} weaponId - The weapon ID
 * @param {Array} targetingData - Database targeting data
 * @returns {Object|null} The targeting rules or null if not found
 */
export const getWeaponTargetingRules = (weaponId, targetingData) => {
  if (!targetingData?.weapons) return null;
  return targetingData.weapons.find((rule) => targetingRuleMatchesId(rule, weaponId)) || null;
};

/**
 * Check if a special unit needs targeting based on database rules
 * @param {number} unitId - The unit ID to check
 * @param {Array} targetingData - Database targeting data
 * @returns {boolean} True if the special unit needs targeting
 */
export const specialUnitNeedsTargeting = (unitId, targetingData) => {
  if (!targetingData?.specialUnits) return false;
  const specialRule = targetingData.specialUnits.find((rule) => targetingRuleMatchesId(rule, unitId));
  return specialRule && specialRule.targeting !== 'none';
};

/**
 * Get targeting rules for a specific special unit
 * @param {number} unitId - The unit ID
 * @param {Array} targetingData - Database targeting data
 * @returns {Object|null} The targeting rules or null if not found
 */
export const getSpecialUnitTargetingRules = (unitId, targetingData) => {
  if (!targetingData?.specialUnits) return null;
  return targetingData.specialUnits.find((rule) => targetingRuleMatchesId(rule, unitId)) || null;
};
